/**
 * Iter 73 Phase 8 — Revision Workspace foundation.
 *
 * Honest revision history surface. Renders:
 *   - Revision History list (every rev of this sheet_number)
 *   - Per-row metadata (number, status, uploader, date)
 *   - Side-by-side foundation (current vs prior) — placeholders only;
 *     no fake auto-callouts, no AI diff. Compare canvas is the existing
 *     RevisionCompare page; this panel surfaces the data honestly.
 *   - Manual review summary (clearly labelled MANUAL, not auto)
 *   - Revision activity feed (audit_log filtered to revision/upload/review)
 *
 * Rules followed:
 *   - No fake AI.
 *   - No fake added/removed/modified counts.
 *   - Manual review counts (if entered) are tagged "MANUAL" in the UI.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, ListChecks } from "lucide-react";
import { api } from "@/lib/api";

export default function RevisionWorkspace({ projectId, sheetId }) {
  const [sheet, setSheet] = useState(null);
  const [revisions, setRevisions] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    if (!sheetId) return;
    let alive = true;
    Promise.all([
      api.get(`/sheets/${sheetId}`).then((r) => r.data),
      api.get(`/sheets/${sheetId}/revisions`).then((r) => r.data),
      api.get(`/sheets/${sheetId}/reviews`).then((r) => r.data).catch(() => []),
      api.get(`/audit`, { params: { target_id: sheetId, limit: 50 } })
        .then((r) => (Array.isArray(r.data) ? r.data : (r.data?.items || [])))
        .catch(() => []),
    ]).then(([s, revs, rvs, act]) => {
      if (!alive) return;
      setSheet(s);
      setRevisions(Array.isArray(revs) ? revs : []);
      setReviews(Array.isArray(rvs) ? rvs : []);
      setActivity(act.filter((a) => /revision|review|upload|ack/i.test(a.action || "")));
    });
    return () => { alive = false; };
  }, [sheetId]);

  if (revisions === null) {
    return <div className="p-4 text-xs text-[var(--fop-text-3)]">Loading revision workspace…</div>;
  }

  const current = revisions.find((r) => r.id === sheetId) || sheet;
  const prior = revisions
    .filter((r) => r.id !== sheetId)
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];

  return (
    <div data-testid="revision-workspace">
      <PanelHeader label={`Revisions · ${revisions.length}`} />
      {revisions.length <= 1 ? (
        <EmptyState
          title="No prior revision available"
          body="Upload a revised version of this sheet to begin revision comparison. Every revision carries its own metadata, audit trail, and review status — there is no automatic AI diff today; reviewers enter change counts manually."
        />
      ) : (
        <RevisionList revisions={revisions} current={current?.id} />
      )}

      {/* Side-by-side foundation */}
      <div className="px-3 py-3 border-t border-[var(--fop-border)]" data-testid="rev-side-by-side">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-2">
          Side-by-Side
        </div>
        <div className="grid grid-cols-2 gap-2">
          <RevisionTile label="Current" revision={current} accent="primary" />
          <RevisionTile label="Prior" revision={prior} accent="muted" />
        </div>
        <a
          href={`/sheet/${sheetId}/compare`}
          className="block mt-2 text-center text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:underline"
          data-testid="open-compare"
        >Open full compare →</a>
      </div>

      {/* Manual review summary */}
      <div className="px-3 py-3 border-t border-[var(--fop-border)]" data-testid="rev-manual-summary">
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
            Review Summary
          </div>
          <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--fop-warn)] text-[var(--fop-warn)]"
                title="Counts are typed in by the reviewer — not auto-detected">MANUAL</span>
        </div>
        {reviews.length === 0 ? (
          <div className="text-xs text-[var(--fop-text-3)]">No review submitted yet.</div>
        ) : (
          reviews.slice(0, 1).map((rv) => {
            const c = rv.counts || {};
            return (
              <div key={rv.id} className="space-y-1.5" data-testid={`rev-summary-${rv.id}`}>
                <CountRow label="Added"     value={c.added ?? "—"} color="#10b981" />
                <CountRow label="Modified"  value={c.modified ?? "—"} color="#fbbf24" />
                <CountRow label="Removed"   value={c.removed ?? "—"} color="#ef4444" />
                <CountRow label="Unchanged" value={c.unchanged ?? "—"} color="#9ca3af" />
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-1.5">
                  Reviewed by {rv.reviewer_name || rv.reviewer_id || "—"}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Field data lineage — carry-forward across revisions */}
      {revisions.length > 1 && <CarryForwardPanel sheetId={sheetId} />}

      {/* R10 (2026-09-10) — linked tasks + preserved field evidence,
          the two parts of the composed revision-impact view that
          weren't surfaced anywhere before this session (carry-forward
          itself was already shown above). */}
      {revisions.length > 1 && <LinkedTasksAndEvidencePanel sheetId={sheetId} />}

      {/* Activity */}
      <div className="px-3 py-3 border-t border-[var(--fop-border)]" data-testid="rev-activity">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-2">
          Revision Activity
        </div>
        {activity.length === 0 ? (
          <div className="text-xs text-[var(--fop-text-3)]">No revision activity yet.</div>
        ) : (
          <ul>
            {activity.slice(0, 12).map((a, i) => (
              <li key={a.id || i} className="py-1.5 border-b border-[var(--fop-border)] last:border-b-0">
                <div className="text-xs text-[var(--fop-text)] truncate">{a.action}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                  {a.actor_name || a.actor_id || "—"}
                  {a.created_at ? ` · ${new Date(a.created_at).toLocaleString()}` : ""}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PanelHeader({ label }) {
  return (
    <div className="px-3 py-2 border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)] font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">{label}</div>
  );
}

function RevisionList({ revisions, current }) {
  const sorted = [...revisions].sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || ""));
  return (
    <ul data-testid="rev-history-list">
      {sorted.map((r) => {
        const isCurrent = r.id === current;
        return (
          <li key={r.id} className="px-3 py-2 border-b border-[var(--fop-border)] flex items-center gap-2" data-testid={`rev-row-${r.id}`}>
            <div className={`w-1 self-stretch rounded-full ${isCurrent ? "bg-[var(--fop-primary)]" : "bg-[var(--fop-border)]"}`} />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[var(--fop-text)] font-medium truncate">
                REV {r.revision ?? "—"} {isCurrent && <span className="ml-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-primary)]">current</span>}
              </div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                {r.uploaded_by_name ? ` · ${r.uploaded_by_name}` : ""}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function RevisionTile({ label, revision, accent }) {
  if (!revision) {
    return (
      <div className="border border-dashed border-[var(--fop-border)] rounded p-2 text-center" data-testid={`rev-tile-${label.toLowerCase()}`}>
        <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)] mb-0.5">{label}</div>
        <div className="text-xs text-[var(--fop-text-3)]">—</div>
      </div>
    );
  }
  const accentCls = accent === "primary"
    ? "border-[var(--fop-primary)]"
    : "border-[var(--fop-border)]";
  return (
    <div className={`border ${accentCls} rounded p-2`} data-testid={`rev-tile-${label.toLowerCase()}`}>
      <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)] mb-0.5">{label}</div>
      <div className="text-xs text-[var(--fop-text)] font-medium">REV {revision.revision ?? "—"}</div>
      <div className="text-[9px] font-mono text-[var(--fop-text-3)] mt-0.5">
        {revision.created_at ? new Date(revision.created_at).toLocaleDateString() : "—"}
      </div>
    </div>
  );
}

function CountRow({ label, value, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
      <span className="text-[11px] text-[var(--fop-text)] flex-1">{label}</span>
      <span className="text-[11px] font-mono text-[var(--fop-text)] font-semibold">{value}</span>
    </div>
  );
}

const CLASSIFICATION_LABEL = {
  SAFE_AUTO_CARRY: "Safe auto-carry",
  CARRY_WITH_VERIFIED_TRANSFORM: "Carry · verified transform",
  REQUIRES_HUMAN_CONFIRMATION: "Requires confirmation",
  DO_NOT_CARRY_HISTORICAL_ONLY: "Historical only",
  REGENERATE_ON_NEW_REVISION: "Regenerated fresh",
  NOT_APPLICABLE: "N/A",
};
const CLASSIFICATION_COLOR = {
  SAFE_AUTO_CARRY: "#10b981",
  CARRY_WITH_VERIFIED_TRANSFORM: "#38bdf8",
  REQUIRES_HUMAN_CONFIRMATION: "#f59e0b",
  DO_NOT_CARRY_HISTORICAL_ONLY: "#9ca3af",
  REGENERATE_ON_NEW_REVISION: "#a78bfa",
  NOT_APPLICABLE: "#6b7280",
};
const DOMAIN_LABEL = {
  pins: "Pins", markups: "Markups", measurements: "Measurements", alignments: "Alignments",
  structures: "Structures (Object Intel)", utility_watches: "Utility Watches", utility_conflicts: "Utility Conflicts",
  scale: "Scale", gps_calibration: "GPS Calibration", project_calibration: "Survey Control Calibration",
  photos: "Photos", ocr_extraction: "OCR / Extraction", status_history: "Status / History",
};
const DOMAIN_ORDER = ["pins", "markups", "measurements", "alignments", "structures", "utility_watches",
  "utility_conflicts", "scale", "gps_calibration", "project_calibration", "photos", "ocr_extraction", "status_history"];

function ClassificationBadge({ tag }) {
  if (!tag) return null;
  return (
    <span
      className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border"
      style={{ color: CLASSIFICATION_COLOR[tag], borderColor: CLASSIFICATION_COLOR[tag] }}
      data-testid={`lineage-classification-${tag.toLowerCase()}`}
    >
      {CLASSIFICATION_LABEL[tag] || tag}
    </span>
  );
}

function CarryForwardPanel({ sheetId }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [error, setError] = useState(null);
  const [hasPrior, setHasPrior] = useState(null);
  const [expandedFlag, setExpandedFlag] = useState(null);

  const loadStatus = () => {
    setLoading(true);
    setError(null);
    api.get(`/sheets/${sheetId}/carry-forward`).then((r) => {
      setHasPrior(!!r.data.has_prior_revision);
      if (r.data.already_run && r.data.result) {
        setResult(r.data.result);
      } else {
        setResult(null);
      }
      setLoading(false);
    }).catch((e) => {
      setError(e?.response?.data?.detail || "Could not load lineage status.");
      setLoading(false);
    });
  };

  const runCarryForward = () => {
    setLoading(true);
    setError(null);
    api.post(`/sheets/${sheetId}/carry-forward`).then((r) => {
      setResult(r.data);
      setLoading(false);
    }).catch((e) => {
      setError(e?.response?.data?.detail || "Could not run carry-forward — you may not have permission.");
      setLoading(false);
    });
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId]);

  const confirmItem = (collection, itemId) => {
    setConfirming(itemId);
    api.post(`/sheets/${sheetId}/carry-forward/confirm`, { collection, item_id: itemId })
      .then(() => api.get(`/sheets/${sheetId}/carry-forward/pending`))
      .then((r) => {
        setResult((prev) => (prev ? { ...prev, pending_confirmation: r.data.pending || {} } : prev));
        setConfirming(null);
      })
      .catch(() => setConfirming(null));
  };

  const describeItem = (item) => {
    const bits = [];
    if (item.label) bits.push(item.label);
    if (item.title) bits.push(item.title);
    if (item.structure_type) bits.push(`(${item.structure_type})`);
    if (item.kind) bits.push(`(${item.kind})`);
    if (item.normalized_x != null && item.normalized_y != null) {
      bits.push(`@ (${item.normalized_x.toFixed(2)}, ${item.normalized_y.toFixed(2)})`);
    }
    return bits.length ? bits.join(" ") : item.id;
  };

  return (
    <div className="px-3 py-3 border-t border-[var(--fop-border)]" data-testid="carry-forward-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
          Field Data Lineage
        </div>
        {result && !loading && (
          <button
            onClick={runCarryForward}
            className="text-[9px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:underline"
            data-testid="carry-forward-retry-btn"
          >re-check</button>
        )}
      </div>

      {loading && (
        <div className="text-xs text-[var(--fop-text-3)]" data-testid="carry-forward-loading">Loading field data lineage…</div>
      )}
      {error && (
        <div className="text-xs text-[var(--fop-error)]" data-testid="carry-forward-error">{error}</div>
      )}

      {!loading && !error && hasPrior === false && (
        <div className="text-xs text-[var(--fop-text-3)]" data-testid="carry-forward-no-prior">
          No prior revision to carry field data from.
        </div>
      )}

      {!loading && !error && hasPrior && !result && (
        <div data-testid="carry-forward-run-prompt">
          <div className="text-xs text-[var(--fop-text-3)] mb-2 leading-relaxed">
            Pins, markups, measurements, and calibration from the prior revision have not been evaluated yet.
          </div>
          <button
            onClick={runCarryForward}
            className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border border-[var(--fop-primary)] text-[var(--fop-primary)] hover:bg-[var(--fop-primary)] hover:text-[var(--fop-surface)]"
            data-testid="carry-forward-run-btn"
          >Run carry-forward</button>
        </div>
      )}

      {result && !loading && (
        <div className="space-y-2" data-testid="carry-forward-results">
          {!result.engine_ok && (
            <div className="text-[11px] text-[var(--fop-warn)]" data-testid="carry-forward-engine-not-ok">
              Change engine could not compare rasters — every dependent record is flagged for manual review rather than silently carried.
            </div>
          )}
          <ul>
            {DOMAIN_ORDER.map((label) => {
              const tag = result.domain_classification?.[label];
              if (!tag) return null;
              const isCalibrationDomain = label === "scale" || label === "gps_calibration" || label === "project_calibration";
              const carriedCount = isCalibrationDomain
                ? (result[label]?.carried ? 1 : 0)
                : (result.carried?.[label] || 0);
              const flagged = result.flagged?.[label];
              const flaggedList = isCalibrationDomain
                ? (result[label] && !result[label].carried ? [result[label]] : [])
                : (Array.isArray(flagged) ? flagged : []);
              const flaggedCount = flaggedList.length;
              return (
                <li key={label} className="py-1 border-b border-[var(--fop-border)] last:border-b-0"
                    data-testid={`lineage-domain-${label}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[var(--fop-text)]">{DOMAIN_LABEL[label] || label}</span>
                    <div className="flex items-center gap-2">
                      {(carriedCount > 0 || flaggedCount > 0) && (
                        <span className="text-[10px] font-mono text-[var(--fop-text-3)]">
                          {carriedCount > 0 && <span title="carried">✓{carriedCount}</span>}
                          {carriedCount > 0 && flaggedCount > 0 && " · "}
                          {flaggedCount > 0 && (
                            <button
                              onClick={() => setExpandedFlag(expandedFlag === label ? null : label)}
                              title="flagged for review — click to see items"
                              className="text-[var(--fop-warn)] hover:underline"
                              data-testid={`lineage-flagged-toggle-${label}`}
                            >⚠{flaggedCount}</button>
                          )}
                        </span>
                      )}
                      <ClassificationBadge tag={tag} />
                    </div>
                  </div>
                  {expandedFlag === label && flaggedCount > 0 && (
                    <ul className="mt-1 pl-2 border-l border-[var(--fop-warn)] space-y-0.5" data-testid={`lineage-flagged-list-${label}`}>
                      {flaggedList.map((item, idx) => {
                        const pt = (item.x != null && item.y != null)
                          ? { nx: item.x, ny: item.y }
                          : Array.isArray(item.points) && item.points[0]
                            ? item.points[0]
                            : null;
                        return (
                          <li key={item.id || idx} className="text-[10px] font-mono text-[var(--fop-text-3)]">
                            {item.id || "—"}{item.reason ? ` · ${item.reason}` : ""}
                            {pt ? ` @ (${pt.nx.toFixed(2)}, ${pt.ny.toFixed(2)})` : ""}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          {Object.keys(result.pending_confirmation || {}).length > 0 && (
            <div className="mt-2 pt-2 border-t border-[var(--fop-border)]" data-testid="carry-forward-pending-section">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-warn)] mb-1.5">
                Pending Human Confirmation
              </div>
              <ul className="space-y-1.5">
                {Object.entries(result.pending_confirmation).flatMap(([collection, items]) =>
                  items.map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-2 py-1"
                        data-testid={`lineage-pending-${item.id}`}>
                      <span className="text-[11px] text-[var(--fop-text)] truncate" title={describeItem(item)}>
                        {DOMAIN_LABEL[collection] || collection} · {describeItem(item)}
                      </span>
                      <button
                        onClick={() => confirmItem(collection, item.id)}
                        disabled={confirming === item.id}
                        className="shrink-0 text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-[var(--fop-primary)] text-[var(--fop-primary)] hover:bg-[var(--fop-primary)] hover:text-[var(--fop-surface)] disabled:opacity-50"
                        data-testid={`lineage-confirm-btn-${item.id}`}
                      >{confirming === item.id ? "confirming…" : "confirm"}</button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ title, body }) {
  return (
    <div className="p-4">
      <div className="text-sm text-[var(--fop-text)] font-medium mb-1.5">{title}</div>
      <div className="text-xs text-[var(--fop-text-3)] leading-relaxed">{body}</div>
    </div>
  );
}

/**
 * R10 (2026-09-10) — linked tasks (pins/workflows tied to this sheet)
 * and preserved field evidence (photos on this sheet, plus photos on
 * the PRIOR sheet — never carried forward by design, but still
 * reachable here so a reviewer can see what evidence existed against
 * the old drawing without it silently vanishing from view once a new
 * revision is promoted).
 */
function LinkedTasksAndEvidencePanel({ sheetId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/sheets/${sheetId}/revision-impact`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load linked tasks/evidence."));
  }, [sheetId]);

  if (error) return null; // non-critical panel; fail silent, carry-forward panel above already surfaces the primary error
  if (!data) return null;

  const pins = data.linked_tasks?.pins || [];
  const workflows = data.linked_tasks?.workflows || [];
  const currentPhotos = data.field_evidence?.current_sheet_photos || [];
  const priorPhotos = data.field_evidence?.prior_sheet_photos || [];
  if (!pins.length && !workflows.length && !currentPhotos.length && !priorPhotos.length) return null;

  return (
    <div className="px-3 py-3 border-t border-[var(--fop-border)]" data-testid="revision-linked-tasks-evidence">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-2 flex items-center gap-1.5">
        <ListChecks size={11} /> Linked tasks &amp; field evidence
      </div>
      {(pins.length > 0 || workflows.length > 0) && (
        <div className="mb-2 space-y-1" data-testid="revision-linked-tasks-list">
          {pins.map((p) => (
            <div key={p.id} className="text-[11px] text-[var(--fop-text-2)] flex items-center gap-1.5">
              <span className="px-1 py-0.5 rounded text-[9px] font-mono uppercase bg-[var(--fop-surface-2)] text-[var(--fop-text-3)]">pin</span>
              {p.title || p.category} <span className="text-[var(--fop-text-3)]">· {p.status}</span>
            </div>
          ))}
          {workflows.map((w) => (
            <div key={w.id} className="text-[11px] text-[var(--fop-text-2)] flex items-center gap-1.5">
              <span className="px-1 py-0.5 rounded text-[9px] font-mono uppercase bg-[var(--fop-surface-2)] text-[var(--fop-text-3)]">{w.type}</span>
              {w.title} <span className="text-[var(--fop-text-3)]">· {w.status}</span>
            </div>
          ))}
        </div>
      )}
      {priorPhotos.length > 0 && (
        <div className="text-[11px] text-amber-500 flex items-center gap-1.5" data-testid="revision-prior-photos-preserved">
          <Camera size={11} /> {priorPhotos.length} field photo{priorPhotos.length === 1 ? "" : "s"} preserved from the prior revision — not carried forward, still viewable on that revision's history.
        </div>
      )}
      {currentPhotos.length > 0 && (
        <div className="text-[11px] text-[var(--fop-text-3)] flex items-center gap-1.5" data-testid="revision-current-photos">
          <Camera size={11} /> {currentPhotos.length} field photo{currentPhotos.length === 1 ? "" : "s"} on this revision.
        </div>
      )}
    </div>
  );
}
